// Phase 4: AuthPage orchestrator - contains ALL state and logic
// Child components are stateless UI only

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import type { PasswordMatchStatus, PasswordStrength } from './types';
import VerificationScreen from './VerificationScreen';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';

export default function AuthPage() {
  // ============ ALL STATE ============
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordMatchStatus, setPasswordMatchStatus] = useState<PasswordMatchStatus>('idle');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: '',
    color: 'bg-muted'
  });
  const [awaitingEmailVerification, setAwaitingEmailVerification] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // ============ ALL LOGIC ============
  
  // Google OAuth handler
  const handleGoogleSignIn = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/timer`,
        queryParams: {
          prompt: 'select_account'
        }
      }
    });
    
    if (error) {
      toast({
        title: "Google Sign-in Failed",
        description: error.message,
        variant: "destructive"
      });
      trackEvent('auth_error', { method: 'google', error: error.message });
      setLoading(false);
    }
  };

  // REMOVED: checkUser effect - per V2 remediation plan
  // Guards in App.tsx are the ONLY routing authority
  // AuthPage should ONLY handle auth operations, not navigation

  // Parse OAuth callback errors from URL
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    
    const error = queryParams.get('oauth_error') || hashParams.get('error');
    const errorDescription = queryParams.get('oauth_error_description') || hashParams.get('error_description');
    
    if (error) {
      const decodedDescription = errorDescription?.replace(/\+/g, ' ') || '';
      let errorTitle = 'Sign-in Failed';
      let userMessage = 'Sign-in failed. Please try again.';
      
      if (decodedDescription.includes('Identity is already linked') || 
          decodedDescription.includes('already linked to another user')) {
        errorTitle = 'Account Already Exists';
        userMessage = 'This email already has a password account. Please sign in with your email and password instead.';
      } else if (error === 'access_denied') {
        errorTitle = 'Sign-in Cancelled';
        userMessage = 'Sign-in was cancelled. Please try again when ready.';
      } else if (error === 'server_error') {
        errorTitle = 'Server Error';
        userMessage = 'Google sign-in is temporarily unavailable. Please try again later or sign in with email.';
      } else if (decodedDescription) {
        userMessage = decodedDescription;
      }
      
      toast({ title: errorTitle, description: userMessage, variant: "destructive", duration: 6000 });
      trackEvent('oauth_callback_error', { error, error_description: errorDescription });
      window.history.replaceState({}, document.title, '/auth');
    }
  }, [toast]);

  const validatePassword = (pwd: string) => {
    const errors = [];
    if (pwd.length < 8) errors.push("at least 8 characters");
    if (!/(?=.*[a-z])/.test(pwd)) errors.push("one lowercase letter");
    if (!/(?=.*[A-Z])/.test(pwd)) errors.push("one uppercase letter");
    if (!/(?=.*\d)/.test(pwd)) errors.push("one number");
    if (!/(?=.*[!@#$%^&*])/.test(pwd)) errors.push("one special character (!@#$%^&*)");
    
    if (errors.length > 0) {
      setPasswordError(`Password must contain ${errors.join(", ")}`);
      return false;
    }
    
    setPasswordError("");
    return true;
  };

  const checkPasswordMatch = () => {
    if (!confirmPassword) {
      setPasswordMatchStatus('idle');
      setPasswordError('');
      return true;
    }
    
    if (password === confirmPassword) {
      setPasswordMatchStatus('matching');
      setPasswordError('');
      return true;
    } else if (confirmPassword.length >= password.length) {
      setPasswordMatchStatus('not-matching');
      setPasswordError('Passwords do not match');
      return false;
    } else {
      setPasswordMatchStatus('idle');
      setPasswordError('');
      return true;
    }
  };

  // Auto-validate password match when either password changes
  useEffect(() => {
    if (!isLogin && (password || confirmPassword)) {
      checkPasswordMatch();
    } else {
      setPasswordMatchStatus('idle');
      setPasswordError('');
    }
  }, [password, confirmPassword, isLogin]);

  // Calculate password strength
  useEffect(() => {
    if (!isLogin && password) {
      let score = 0;
      
      if (password.length >= 8) score++;
      if (password.length >= 12) score++;
      if (/[A-Z]/.test(password)) score++;
      if (/[0-9]/.test(password)) score++;
      if (/[^A-Za-z0-9]/.test(password)) score++;

      const feedback = [
        'Very Weak - Add uppercase, numbers, and symbols',
        'Weak - Add more complexity',
        'Fair - Consider adding symbols',
        'Good - Secure password',
        'Excellent - Very secure password'
      ];

      const colors = [
        'bg-red-500',
        'bg-orange-500',
        'bg-yellow-500',
        'bg-green-500',
        'bg-emerald-500'
      ];

      setPasswordStrength({
        score: Math.min(score, 4),
        feedback: feedback[Math.min(score, 4)],
        color: colors[Math.min(score, 4)]
      });
    } else {
      setPasswordStrength({ score: 0, feedback: '', color: 'bg-muted' });
    }
  }, [password, isLogin]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isLogin && !agreedToTerms) {
      toast({
        title: "Terms Required",
        description: "Please agree to the Terms of Service and Privacy Policy",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    console.log('[Auth] 🚀 handleAuth started', { isLogin, email, agreedToTerms });

    if (!isLogin && (!validatePassword(password) || password !== confirmPassword)) {
      console.log('[Auth] ❌ Password validation failed');
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        console.log('[Auth] 🔐 Attempting login for:', email);
        const { error } = await supabase.auth.signInWithPassword({ email, password });

        if (error) {
          if (error.message.includes('Email not confirmed')) {
            console.log('[Auth] 🔄 Login blocked - email not verified');
            setAwaitingEmailVerification(true);
            toast({
              title: "Email Not Verified",
              description: "Please check your email for the verification link.",
            });
            setLoading(false);
            return;
          }

          let errorDescription = error.message;
          if (error.message.includes('Invalid login credentials')) {
            errorDescription = "Incorrect email or password. Please try again.";
          } else if (error.message.toLowerCase().includes('network')) {
            errorDescription = "Network issue. Please check your connection and try again.";
          }

          toast({
            title: "Sign-in failed",
            description: errorDescription,
            variant: "destructive"
          });

          setLoading(false);
          return;
        }

        trackEvent('user_login', { method: 'email' });
        toast({ title: "Welcome back!", description: "Successfully signed in." });
        // REMOVED: navigate('/timer') - per V2 remediation plan
        // Guards in App.tsx handle navigation after auth state changes
        // The auth state change will trigger re-render → guards redirect
      } else {
        console.log('[Auth] 📝 Attempting signup for:', email);
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/onboarding`
          }
        });

        if (error?.message?.includes('User already registered')) {
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Please sign in instead.",
          });
          setIsLogin(true);
          setLoading(false);
          return;
        }

        if (error) throw error;

        trackEvent('user_signup', { method: 'email' });
        console.log('[Auth] 📧 Signup complete - showing verification screen');
        setAwaitingEmailVerification(true);
        toast({
          title: "Check Your Email",
          description: "We've sent a verification link to complete your signup.",
        });
      }
    } catch (error: any) {
      console.error('[Auth] ❌ Authentication error caught:', error);
      toast({
        title: "Authentication Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      console.log('[Auth] ✅ handleAuth completed');
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    console.log('[Auth] 🔁 Resend verification clicked', { email });
    setResendingEmail(true);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/onboarding`,
        },
      });

      if (error) throw error;

      toast({
        title: 'Email Sent!',
        description: 'Please check your inbox (and spam folder) for the verification link.',
      });
    } catch (error: any) {
      console.error('[Auth] Resend verification failed', error);
      toast({
        title: 'Failed to Resend',
        description: error?.message || 'Please try again in a few moments.',
        variant: 'destructive',
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setAwaitingEmailVerification(false);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setAgreedToTerms(false);
  };

  const handleSwitchMode = () => {
    setIsLogin(!isLogin);
    setAgreedToTerms(false);
  };

  // Navigation handlers
  const onNavigateToPrivacy = () => navigate('/privacy-policy');
  const onNavigateToTerms = () => navigate('/terms-of-service');
  const onNavigateToDataDeletion = () => navigate('/data-deletion');
  const onForgotPassword = () => navigate('/forgot-password');

  // ============ RENDER ============
  
  if (awaitingEmailVerification) {
    return (
      <VerificationScreen
        email={email}
        resending={resendingEmail}
        onResend={handleResendVerification}
        onUseDifferentEmail={handleUseDifferentEmail}
      />
    );
  }

  if (isLogin) {
    return (
      <LoginForm
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        loading={loading}
        passwordError={passwordError}
        onSubmit={handleAuth}
        onGoogleSignIn={handleGoogleSignIn}
        onForgotPassword={onForgotPassword}
        onSwitchToSignup={handleSwitchMode}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToDataDeletion={onNavigateToDataDeletion}
      />
    );
  }

  return (
    <SignupForm
      email={email}
      setEmail={setEmail}
      password={password}
      setPassword={setPassword}
      confirmPassword={confirmPassword}
      setConfirmPassword={setConfirmPassword}
      showPassword={showPassword}
      setShowPassword={setShowPassword}
      showConfirmPassword={showConfirmPassword}
      setShowConfirmPassword={setShowConfirmPassword}
      agreedToTerms={agreedToTerms}
      setAgreedToTerms={setAgreedToTerms}
      loading={loading}
      passwordError={passwordError}
      passwordStrength={passwordStrength}
      passwordMatchStatus={passwordMatchStatus}
      onSubmit={handleAuth}
      onGoogleSignIn={handleGoogleSignIn}
      onSwitchToLogin={handleSwitchMode}
      onNavigateToPrivacy={onNavigateToPrivacy}
      onNavigateToTerms={onNavigateToTerms}
      onNavigateToDataDeletion={onNavigateToDataDeletion}
    />
  );
}
